import { useState, useEffect } from "react";
import { LayoutDashboard, FileText, Users, LogOut, ClipboardCheck, UserCog, Settings, User, RefreshCw, CreditCard, MessageSquare, ChevronDown, Shield, HelpCircle, BarChart3, CalendarDays, FileBox, FileBarChart, Vote, ClipboardList, Eye, BookOpen, Clock, Target } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  SidebarSeparator,
} from "@/components/ui/sidebar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useUserRole } from "@/hooks/useUserRole";
import { useIsSuperAdmin } from "@/hooks/useIsSuperAdmin";
import { AnimatedLogo } from "@/components/AnimatedLogo";
import { NotificationBell } from "@/components/NotificationBell";
import { useTimeOffAccess } from "@/hooks/useTimeOffAccess";

const basePersonalItems = [
  { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
  { title: "My Evaluations", url: "/my-reviews", icon: ClipboardCheck },
  { title: "My Goals", url: "/goals", icon: Target },
  { title: "My Surveys & Polls", url: "/my-surveys", icon: ClipboardList },
  { title: "My Profile", url: "/my-profile", icon: User },
];

const timeOffPersonalItems = [
  { title: "My Time Off", url: "/my-time-off", icon: CalendarDays },
  { title: "My Timesheets", url: "/my-timesheets", icon: Clock },
];

const contentItems = (isOwner: boolean, role: string | null) => 
  (isOwner || role === "manager" ? [
    { title: "Evaluation Designer", url: "/templates", icon: FileText },
    { title: "Schedule", url: "/cycles", icon: RefreshCw },
    { title: "View Evaluations", url: "/view-evaluations", icon: Eye },
    { title: "Manager Feedback", url: "/manager-feedback", icon: MessageSquare },
    { title: "Goals", url: "/team-goals", icon: Target },
    { title: "Analytics", url: "/analytics", icon: BarChart3 },
  ] : []);

const teamItems = (isOwner: boolean, role: string | null) => 
  (isOwner || role === "manager" ? [
    { title: "Staff Members", url: "/staff-members", icon: Users },
    { title: "Teams", url: "/teams", icon: UserCog }
  ] : []);

const timeOffItems = (isOwner: boolean, role: string | null) =>
  (isOwner || role === "manager" ? [
    { title: "Time Off", url: "/calendar", icon: CalendarDays, hasBadge: true },
    { title: "Roster", url: "/roster", icon: Users },
    { title: "Leave Policies", url: "/leave-policies", icon: FileBox },
    { title: "Leave Reports", url: "/leave-reports", icon: FileBarChart },
    { title: "Timesheets", url: "/timesheets", icon: Clock },
    { title: "Time Off Settings", url: "/time-off-settings", icon: Settings },
  ] : []);

const surveyItems = (isOwner: boolean, role: string | null) =>
  (isOwner || role === "manager" ? [
    { title: "Poll Designer", url: "/poll-designer", icon: Vote },
    { title: "Survey Designer", url: "/survey-designer", icon: FileText },
    { title: "Schedule", url: "/survey-schedule", icon: RefreshCw },
    { title: "View Results", url: "/survey-results", icon: BarChart3 },
  ] : []);

// mySurveyItems removed - now part of personalItems

const accountItems = (isOwner: boolean, role: string | null) => [
  ...(isOwner || role === "manager" ? [
    { title: "Settings", url: "/settings", icon: Settings },
    { title: "Plan & Billing", url: "/plan-billing", icon: CreditCard },
  ] : []),
  { title: "Help Center", url: "/help-center", icon: BookOpen },
  { title: "Support", url: "/support", icon: HelpCircle },
];

const adminItems = [
  { title: "Admin Dashboard", url: "/admin", icon: LayoutDashboard },
  { title: "User Management", url: "/admin/users", icon: Users },
  { title: "Support Tickets", url: "/admin/support", icon: MessageSquare },
  { title: "Help Center", url: "/admin/help-center", icon: HelpCircle },
  { title: "Blog", url: "/admin/blog", icon: BookOpen },
  { title: "Email Templates", url: "/admin/email-templates", icon: FileText },
  { title: "Activity Log", url: "/admin/activity-log", icon: Shield },
];

export function AppSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOwner, role } = useUserRole();
  const { isSuperAdmin } = useIsSuperAdmin();
  const { hasTimeOffAccess } = useTimeOffAccess();
  const [managerFeedbackCount, setManagerFeedbackCount] = useState(0);
  const [pendingTimeOffCount, setPendingTimeOffCount] = useState(0);
  const personalItems = [...basePersonalItems, ...(hasTimeOffAccess ? timeOffPersonalItems : [])];
  const [pendingSurveysCount, setPendingSurveysCount] = useState(0);
  
  // Collapsible section states - all collapsed by default for a tidy initial view
  const [contentOpen, setContentOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [timeOffOpen, setTimeOffOpen] = useState(false);
  const [surveyOpen, setSurveyOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);

  // Clear sidebar section preferences on fresh login (no persistence between sessions)
  useEffect(() => {
    // We intentionally don't load from localStorage on mount
    // This ensures a clean, collapsed sidebar on each login
    localStorage.removeItem('sidebar-sections');
  }, []);

  // Note: We no longer persist sidebar section states between sessions
  // Each login starts with all sections collapsed for a tidy view

  // Refresh badge counts on mount and when navigating between pages
  useEffect(() => {
    const refreshCounts = () => {
      if (isOwner || role === "manager") {
        loadManagerFeedbackCount();
        if (hasTimeOffAccess) {
          loadPendingTimeOffCount();
        }
      }
      // Load pending surveys for all users
      loadPendingSurveysCount();
    };

    refreshCounts();
  }, [isOwner, role, location.pathname]);

  // Refresh counts when browser tab becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (isOwner || role === "manager") {
          loadManagerFeedbackCount();
          if (hasTimeOffAccess) {
            loadPendingTimeOffCount();
          }
        }
        loadPendingSurveysCount();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isOwner, role]);

  // Periodic refresh every 60 seconds for changes from other tabs/users
  useEffect(() => {
    const interval = setInterval(() => {
      if (isOwner || role === "manager") {
        loadManagerFeedbackCount();
        if (hasTimeOffAccess) {
          loadPendingTimeOffCount();
        }
      }
      loadPendingSurveysCount();
    }, 60000);

    return () => clearInterval(interval);
  }, [isOwner, role]);

  const loadManagerFeedbackCount = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { count } = await supabase
        .from("review_assignments")
        .select("*", { count: "exact", head: true })
        .eq("manager_id", user.id)
        .eq("status", "submitted");

      setManagerFeedbackCount(count || 0);
    } catch (error) {
      console.error("Error loading manager feedback count:", error);
    }
  };

  const loadPendingTimeOffCount = async () => {
    try {
      const { count } = await supabase
        .from("time_off_requests")
        .select("*", { count: "exact", head: true })
        .eq("status", "pending");

      setPendingTimeOffCount(count || 0);
    } catch (error) {
      console.error("Error loading pending time off count:", error);
    }
  };

  const loadPendingSurveysCount = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get user's team_id
      const { data: profile } = await supabase
        .from("profiles")
        .select("team_id")
        .eq("id", user.id)
        .single();

      if (!profile?.team_id) {
        setPendingSurveysCount(0);
        return;
      }

      // Get live survey instances where user's team is selected
      const { data: liveInstances } = await supabase
        .from("survey_instances")
        .select("id, selected_team_ids")
        .eq("status", "live");

      if (!liveInstances || liveInstances.length === 0) {
        setPendingSurveysCount(0);
        return;
      }

      // Filter to instances where user's team is included
      const relevantInstances = liveInstances.filter(instance => 
        instance.selected_team_ids.includes(profile.team_id)
      );

      if (relevantInstances.length === 0) {
        setPendingSurveysCount(0);
        return;
      }

      // Get user's participation records
      const { data: participations } = await supabase
        .from("survey_participation")
        .select("instance_id")
        .eq("user_id", user.id);

      const participatedIds = new Set(participations?.map(p => p.instance_id) || []);
      
      // Count instances user hasn't participated in
      const pendingCount = relevantInstances.filter(i => !participatedIds.has(i.id)).length;
      setPendingSurveysCount(pendingCount);
    } catch (error) {
      console.error("Error loading pending surveys count:", error);
    }
  };
  
  // Helper to check if route is active (including nested routes)
  const isActive = (path: string) => {
    // My Evaluations - only highlight on exact path
    if (path === "/my-reviews") {
      return location.pathname === path;
    }
    // Dashboard should only match exactly
    if (path === "/dashboard") {
      return location.pathname === path;
    }
    // Manager Feedback - exact match only, don't include /assignments routes
    if (path === "/manager-feedback") {
      return location.pathname === path;
    }
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error("Failed to log out");
    } else {
      toast.success("Logged out successfully");
      navigate("/auth");
    }
  };

  return (
    <Sidebar className="w-[220px]">
      <SidebarHeader className="border-b border-sidebar-border bg-[#081C3D] p-4">
        <div className="flex items-center justify-between">
          <AnimatedLogo />
          <NotificationBell />
        </div>
      </SidebarHeader>

      <SidebarContent>
        <ScrollArea className="h-[calc(100vh-140px)]">
          {/* Group 1: Personal (always visible, not collapsible) */}
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {personalItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild>
                      <Link
                        to={item.url}
                        className={
                          isActive(item.url)
                            ? "bg-sidebar-accent text-sidebar-accent-foreground font-semibold border-l-4 border-primary"
                            : "hover:bg-sidebar-accent/50"
                        }
                      >
                        <item.icon className="w-4 h-4" />
                        <span>{item.title}</span>
                        {item.url === "/my-surveys" && pendingSurveysCount > 0 && (
                          <Badge 
                            variant={isActive(item.url) ? "secondary" : "outline"}
                            className={`ml-auto text-xs ${
                              isActive(item.url) 
                                ? "" 
                                : "bg-orange-500/20 border-orange-500/30 text-orange-300 font-medium"
                            }`}
                          >
                            {pendingSurveysCount}
                          </Badge>
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* Only show Review Management if user is owner or manager */}
          {(isOwner || role === "manager") && (
            <>
              <SidebarSeparator />

              {/* Group 2: Content Management (collapsible) */}
              <Collapsible open={contentOpen} onOpenChange={setContentOpen}>
                <SidebarGroup>
                  <CollapsibleTrigger asChild>
                    <SidebarGroupLabel className="flex items-center justify-between cursor-pointer hover:bg-sidebar-accent/30 rounded px-2 py-1 transition-colors group">
                      <span>Performance Management</span>
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${contentOpen ? '' : '-rotate-90'}`} />
                    </SidebarGroupLabel>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarGroupContent>
                      <SidebarMenu>
                        {contentItems(isOwner, role).map((item) => (
                          <SidebarMenuItem key={item.title}>
                            <SidebarMenuButton asChild>
                              <Link
                                to={item.url}
                                className={
                                  isActive(item.url)
                                    ? "bg-sidebar-accent text-sidebar-accent-foreground font-semibold border-l-4 border-primary"
                                    : "hover:bg-sidebar-accent/50"
                                }
                              >
                                <item.icon className="w-4 h-4" />
                                <span>{item.title}</span>
                                {item.url === "/manager-feedback" && managerFeedbackCount > 0 && (
                                  <Badge 
                                    variant={isActive(item.url) ? "secondary" : "outline"}
                                    className={`ml-auto text-xs ${
                                      isActive(item.url) 
                                        ? "" 
                                        : "bg-orange-500/20 border-orange-500/30 text-orange-300 font-medium"
                                    }`}
                                  >
                                    {managerFeedbackCount}
                                  </Badge>
                                )}
                              </Link>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        ))}
                      </SidebarMenu>
                    </SidebarGroupContent>
                  </CollapsibleContent>
                </SidebarGroup>
              </Collapsible>
            </>
          )}

          {/* Surveys & Polls section (collapsible) - managers/owners only */}
          {(isOwner || role === "manager") && (
            <>
              <SidebarSeparator />
              <Collapsible open={surveyOpen} onOpenChange={setSurveyOpen}>
                <SidebarGroup>
                  <CollapsibleTrigger asChild>
                    <SidebarGroupLabel className="flex items-center justify-between cursor-pointer hover:bg-sidebar-accent/30 rounded px-2 py-1 transition-colors group">
                      <span>Surveys & Polls</span>
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${surveyOpen ? '' : '-rotate-90'}`} />
                    </SidebarGroupLabel>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarGroupContent>
                      <SidebarMenu>
                        {surveyItems(isOwner, role).map((item) => (
                          <SidebarMenuItem key={item.title}>
                            <SidebarMenuButton asChild>
                              <Link
                                to={item.url}
                                className={
                                  isActive(item.url)
                                    ? "bg-sidebar-accent text-sidebar-accent-foreground font-semibold border-l-4 border-primary"
                                    : "hover:bg-sidebar-accent/50"
                                }
                              >
                                <item.icon className="w-4 h-4" />
                                <span>{item.title}</span>
                              </Link>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        ))}
                      </SidebarMenu>
                    </SidebarGroupContent>
                  </CollapsibleContent>
                </SidebarGroup>
              </Collapsible>
            </>
          )}

          {/* Conditionally show Team Management section (collapsible) */}
          {(isOwner || role === "manager") && (
            <>
              <SidebarSeparator />
              <Collapsible open={teamOpen} onOpenChange={setTeamOpen}>
                <SidebarGroup>
                  <CollapsibleTrigger asChild>
                    <SidebarGroupLabel className="flex items-center justify-between cursor-pointer hover:bg-sidebar-accent/30 rounded px-2 py-1 transition-colors group">
                      <span>Our People</span>
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${teamOpen ? '' : '-rotate-90'}`} />
                    </SidebarGroupLabel>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarGroupContent>
                      <SidebarMenu>
                        {teamItems(isOwner, role).map((item) => (
                          <SidebarMenuItem key={item.title}>
                            <SidebarMenuButton asChild>
                              <Link
                                to={item.url}
                                className={
                                  isActive(item.url)
                                    ? "bg-sidebar-accent text-sidebar-accent-foreground font-semibold border-l-4 border-primary"
                                    : "hover:bg-sidebar-accent/50"
                                }
                              >
                                <item.icon className="w-4 h-4" />
                                <span>{item.title}</span>
                              </Link>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        ))}
                      </SidebarMenu>
                    </SidebarGroupContent>
                  </CollapsibleContent>
                </SidebarGroup>
              </Collapsible>
            </>
          )}

          {/* Time Off & Roster section (collapsible) - BETA: Hidden until feature is complete */}
          {hasTimeOffAccess && (isOwner || role === "manager") && (
            <>
              <SidebarSeparator />
              <Collapsible open={timeOffOpen} onOpenChange={setTimeOffOpen}>
                <SidebarGroup>
                  <CollapsibleTrigger asChild>
                    <SidebarGroupLabel className="flex items-center justify-between cursor-pointer hover:bg-sidebar-accent/30 rounded px-2 py-1 transition-colors group">
                      <span>Time Off & Roster (beta)</span>
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${timeOffOpen ? '' : '-rotate-90'}`} />
                    </SidebarGroupLabel>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarGroupContent>
                      <SidebarMenu>
                        {timeOffItems(isOwner, role).map((item) => (
                          <SidebarMenuItem key={item.title}>
                            <SidebarMenuButton asChild>
                              <Link
                                to={item.url}
                                className={
                                  isActive(item.url)
                                    ? "bg-sidebar-accent text-sidebar-accent-foreground font-semibold border-l-4 border-primary"
                                    : "hover:bg-sidebar-accent/50"
                                }
                              >
                                <item.icon className="w-4 h-4" />
                                <span>{item.title}</span>
                                {item.url === "/calendar" && pendingTimeOffCount > 0 && (
                                  <Badge 
                                    variant={isActive(item.url) ? "secondary" : "outline"}
                                    className={`ml-auto text-xs ${
                                      isActive(item.url) 
                                        ? "" 
                                        : "bg-orange-500/20 border-orange-500/30 text-orange-300 font-medium"
                                    }`}
                                  >
                                    {pendingTimeOffCount}
                                  </Badge>
                                )}
                              </Link>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        ))}
                      </SidebarMenu>
                    </SidebarGroupContent>
                  </CollapsibleContent>
                </SidebarGroup>
              </Collapsible>
            </>
          )}

          <SidebarSeparator />

          {/* Group 4: Account (collapsible) */}
          <Collapsible open={accountOpen} onOpenChange={setAccountOpen}>
            <SidebarGroup>
              <CollapsibleTrigger asChild>
                <SidebarGroupLabel className="flex items-center justify-between cursor-pointer hover:bg-sidebar-accent/30 rounded px-2 py-1 transition-colors group">
                  <span>Account</span>
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${accountOpen ? '' : '-rotate-90'}`} />
                </SidebarGroupLabel>
              </CollapsibleTrigger>
              <CollapsibleContent>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {accountItems(isOwner, role).map((item) => (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton asChild>
                          <Link
                            to={item.url}
                            className={
                              isActive(item.url)
                                ? "bg-sidebar-accent text-sidebar-accent-foreground font-semibold border-l-4 border-primary"
                                : "hover:bg-sidebar-accent/50"
                            }
                          >
                            <item.icon className="w-4 h-4" />
                            <span>{item.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </CollapsibleContent>
            </SidebarGroup>
          </Collapsible>

          {/* Group 5: Super Admin (collapsible, only visible to super_admin) */}
          {isSuperAdmin && (
            <>
              <SidebarSeparator />
              <Collapsible open={adminOpen} onOpenChange={setAdminOpen}>
                <SidebarGroup>
                  <CollapsibleTrigger asChild>
                    <SidebarGroupLabel className="flex items-center justify-between cursor-pointer hover:bg-sidebar-accent/30 rounded px-2 py-1 transition-colors group">
                      <span>Support Admin</span>
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${adminOpen ? '' : '-rotate-90'}`} />
                    </SidebarGroupLabel>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarGroupContent>
                      <SidebarMenu>
                        {adminItems.map((item) => (
                          <SidebarMenuItem key={item.title}>
                            <SidebarMenuButton asChild>
                              <Link
                                to={item.url}
                                className={
                                  isActive(item.url)
                                    ? "bg-sidebar-accent text-sidebar-accent-foreground font-semibold border-l-4 border-primary"
                                    : "hover:bg-sidebar-accent/50"
                                }
                              >
                                <item.icon className="w-4 h-4" />
                                <span>{item.title}</span>
                              </Link>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        ))}
                      </SidebarMenu>
                    </SidebarGroupContent>
                  </CollapsibleContent>
                </SidebarGroup>
              </Collapsible>
            </>
          )}
        </ScrollArea>
      </SidebarContent>

      <SidebarFooter className="p-4">
        <Button 
          onClick={handleLogout}
          variant="ghost" 
          className="w-full justify-start text-muted-foreground hover:text-foreground"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
}
